import axios from 'axios';

// Called directly from the browser per product decision — this key is visible
// in the built JS bundle to anyone who inspects it.
const TWELVELABS_API_KEY = 'tlk_1KZ65W70ERAJA421N3DB409ZN3FE';
const TWELVELABS_BASE_URL = 'https://api.twelvelabs.io/v1.3';

// TwelveLabs error responses are typically { code, message }, but fall back
// through a few other common shapes (and the raw axios error) so callers
// always get something readable to show the user instead of a generic string.
export function extractApiErrorMessage(err, fallback = 'Something went wrong.') {
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    const msg = data?.message || data?.error?.message || data?.error || data?.detail;
    if (typeof msg === 'string' && msg.trim()) return msg;
    if (typeof err?.message === 'string' && err.message.trim()) return err.message;
    return fallback;
}

export function resolveTwelveLabsIds(item) {
    const indexId = item?.indexId || item?.index_id || item?.userMetadata?.indexId || item?.userMetadata?.index_id;
    const videoId = item?.videoId || item?.video_id;
    return { indexId, videoId };
}

export async function fetchTwelveLabsVideo(indexId, videoId) {
    if (!indexId || !videoId) return null;
    const response = await axios.get(
        `${TWELVELABS_BASE_URL}/indexes/${indexId}/videos/${videoId}`,
        {
            headers: { 'x-api-key': TWELVELABS_API_KEY },
            params: { transcription: true },
        }
    );
    return response.data;
}

export async function fetchTwelveLabsEntities({ name = '', status = 'ready', page = 1 } = {}) {
    const response = await axios.get(`${TWELVELABS_BASE_URL}/entities`, {
        headers: { 'x-api-key': TWELVELABS_API_KEY },
        params: { name, status, page },
    });
    const data = response.data;
    const list = data?.data || data?.entities || (Array.isArray(data) ? data : []);
    return { list, pageInfo: data?.page_info || data?.pageInfo || null };
}

// Creates an "analyze" segmentation task for a video. Returns the raw response —
// callers should read whichever id field the account's API version returns
// (id / _id / task_id).
export async function createSegmentAnalysisTask(assetId, segmentDefinition) {
    const response = await axios.post(
        `${TWELVELABS_BASE_URL}/analyze/tasks`,
        {
            model_name: 'pegasus1.5',
            video: { type: 'asset_id', asset_id: assetId },
            analysis_mode: 'time_based_metadata',
            response_format: {
                type: 'segment_definitions',
                segment_definitions: [segmentDefinition],
                segment_time_format: 'seconds',
            },
            temperature: 0,
            max_tokens: 32768,
        },
        { headers: { 'x-api-key': TWELVELABS_API_KEY, 'Content-Type': 'application/json' } }
    );
    return response.data;
}

// Creates a free-text (or, with jsonSchema, structured-JSON) "general" analyze
// task (e.g. summarize, hashtags/topics) — as opposed to createSegmentAnalysisTask's
// time-based segment_definitions mode.
export async function createGeneralAnalysisTask(assetId, prompt, { temperature = 0.2, maxTokens = 4096, jsonSchema } = {}) {
    const response = await axios.post(
        `${TWELVELABS_BASE_URL}/analyze/tasks`,
        {
            model_name: 'pegasus1.5',
            video: { type: 'asset_id', asset_id: assetId },
            analysis_mode: 'general',
            prompt_v2: { input_text: prompt },
            ...(jsonSchema ? { response_format: { type: 'json_schema', json_schema: jsonSchema } } : {}),
            temperature,
            max_tokens: maxTokens,
        },
        { headers: { 'x-api-key': TWELVELABS_API_KEY, 'Content-Type': 'application/json' } }
    );
    return response.data;
}

// NOTE: this endpoint/shape is not confirmed by TwelveLabs docs we have — it
// mirrors their other REST resources (GET .../tasks/:id). If it 404s, callers
// should fall back to the playground.twelvelabs.io/segment?task_id=... link.
export async function fetchAnalysisTask(taskId) {
    const response = await axios.get(`${TWELVELABS_BASE_URL}/analyze/tasks/${taskId}`, {
        headers: { 'x-api-key': TWELVELABS_API_KEY },
    });
    return response.data;
}
